/**
 * MilestoneToast — celebrates course progress crossings at 25 / 50 / 75%.
 * (100% has its own certificate-issue flow, we skip it here.)
 *
 * Renders a floating card with:
 *   - the milestone headline + sub-copy
 *   - a live preview of the auto-generated OG share card (1200×630 PNG)
 *   - "Share" button (uses navigator.share when supported, falling back to
 *     copying the image URL + a text snippet)
 *   - "Download" link that pulls the PNG directly for LinkedIn / Instagram
 */
import React, { useEffect, useState } from 'react';
import { Share2, X, Sparkles, Download } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';

const COPY = {
  25: { headline: 'You\u2019re 25% in — great start!', sub: 'Momentum is 90% of learning. One lesson at a time.' },
  50: { headline: 'Halfway there!', sub: 'You\u2019ve unlocked the full skeleton of this course. Time to consolidate.' },
  75: { headline: '75% done — home stretch!', sub: 'Just a few lessons left before you earn your badge.' },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MilestoneToast = ({ milestone, courseTitle, courseId, holderName, onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!milestone) return undefined;
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, [milestone]);

  if (!milestone) return null;
  const copy = COPY[milestone];
  if (!copy) return null;

  const cardId = courseId || 'course';
  const holderQS = holderName ? `&holder=${encodeURIComponent(holderName)}` : '';
  const cardUrl = `${BACKEND_URL}/api/milestone/course/${cardId}/share-card.png?pct=${milestone}${holderQS}`;

  const shareMsg = `I just hit ${milestone}% in "${courseTitle}" on Freeze Dry Academy!`;
  const learnUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/course/${cardId}`
    : '';

  const doShare = async () => {
    try {
      // Prefer sharing the image file itself — LinkedIn / IG accept this.
      if (navigator.share) {
        try {
          const res = await fetch(cardUrl);
          const blob = await res.blob();
          const file = new File([blob], `fda-milestone-${milestone}.png`, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'My learning milestone',
              text: shareMsg,
              url: learnUrl,
              files: [file],
            });
            return;
          }
          await navigator.share({ title: 'My learning milestone', text: shareMsg, url: learnUrl });
          return;
        } catch (fileErr) {
          /* fall through to clipboard */
        }
      }
      await navigator.clipboard.writeText(`${shareMsg} ${learnUrl}\n${cardUrl}`);
      sonnerToast.success('Copied — paste anywhere; the image link is included.');
    } catch { /* user cancelled */ }
  };

  const doDownload = () => {
    // Open the image in a new tab; users can right-click → save, or the
    // Content-Disposition header will suggest a filename in some browsers.
    window.open(cardUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`fixed bottom-6 right-6 z-[120] w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl border border-primary/40 shadow-2xl bg-gradient-to-br from-primary/95 to-secondary/95 text-primary-foreground p-5 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      data-testid="milestone-toast"
      role="status"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/20 hover:bg-black/40 flex items-center justify-center"
        aria-label="Dismiss milestone toast"
        data-testid="milestone-toast-close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-white/25 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-extrabold uppercase tracking-widest opacity-80">
            {milestone}% milestone
          </div>
          <div className="font-heading text-lg font-extrabold leading-tight mt-0.5">
            {copy.headline}
          </div>
          <p className="text-sm opacity-90 mt-1">{copy.sub}</p>
        </div>
      </div>

      {/* OG image preview */}
      <div className="mt-3 overflow-hidden rounded-xl border border-white/20 bg-navy-900">
        <img
          src={cardUrl}
          alt={`${milestone}% course milestone share card`}
          className="w-full h-auto block"
          loading="lazy"
          data-testid="milestone-toast-card-preview"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={doShare}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-primary px-3 py-1.5 text-xs font-extrabold hover:bg-white/90 transition-colors"
          data-testid="milestone-toast-share"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button
          type="button"
          onClick={doDownload}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/15 text-white px-3 py-1.5 text-xs font-extrabold hover:bg-white/25 transition-colors"
          data-testid="milestone-toast-download"
        >
          <Download className="w-3.5 h-3.5" /> Download
        </button>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-xs font-bold opacity-90 hover:opacity-100 underline"
          data-testid="milestone-toast-dismiss"
        >
          Keep going
        </button>
      </div>
    </div>
  );
};

export default MilestoneToast;
